import React, {Fragment, useEffect, useMemo} from "react"
import {useTable, usePagination, useExpanded, useBlockLayout, useSortBy, useFilters} from "react-table"
import ReactPagination from "react-paginate"
// UI
import {P} from "@uprise/text"
import {H4} from "@uprise/headings"
import Preloader from "../Preloader/Preloader"
import SelectColumnFilter from "./Filters"
import Styled from "styled-components"
// Styles
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableHeader,
	TableData,
	Pagination,
	PaginationButtonContainer,
	PaginationButton,
	PaginationIndex,
	RightIconSpan,
	LeftIconSpan,
	NextButtonIcon,
	BackButtonIcon,
	NoDataWrap,
	PaginationContainer
} from "./table.style"
import styles from "./index.module.css"

const SET_PAGE_INDEX = "SET_PAGE_INDEX"

const TableSortedHeader = Styled.span`
	display: flex;
	justify-content: ${props => props.align};
	align-items: center;
	position: relative;
	padding-right: 12px;
`

const SortIcon = Styled.span`
	position: absolute;
	right: 10px;
`

const ArrowIcon = Styled.span`
	border: ${props => (props.sortingUsed ? "solid #7d60ff" : "solid #ccc")};
	border-width: 0 1px 1px 0;
	padding: 3px;
	display: inline-block;
	transform: ${props => (props.direction === 1 ? "rotate(45deg)" : "rotate(225deg)")};
	transition: transform 0.1s linear;
	margin-left: 10px;
	top: ${props => (props.direction === 1 ? "-3px" : "-1px")};
    	position: relative;

	&:hover {
		cursor: pointer;
	}
`

export const PAGE_SIZE = 20

const TableComponent = ({
	columns,
	data,
	fetchData,
	pageCount: controlledPageCount,
	loading = true,
	isPaginated = true,
	autoResetPage = false,
	alignHeader = "center",
	pageNo,
	...props
}) => {
	const defaultColumn = useMemo(
		() => ({
			// minWidth: 20,
			// maxWidth: 115
		      //Filter: SelectColumnFilter,
		}),
		[]
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		setHiddenColumns,
		dispatch,
		state: {pageIndex, pageSize}
	} = useTable(
		{
			columns,
			data,
			defaultColumn,
			initialState: {
				pageIndex: 0,
				pageSize: PAGE_SIZE,
				hiddenColumns: columns.filter(column => !column.show).map(column => column.id),
				sortBy: columns.map( col => {
					if (typeof col.desc !== 'undefined')
						return ({...col, id: col.accessor, desc: col.desc})
					else return col
				})
			},
			stateReducer: (newState, action, prevState) => {
				switch (action.type) {
					case SET_PAGE_INDEX:
						return {
							...newState,
							pageIndex: action.payload
						}
					default:
						return newState
				}
			},
			manualPagination: true,
			// manualSortBy: true,
			autoResetPage,
			pageCount: controlledPageCount
		},
		useFilters,
		useSortBy,
		useExpanded,
		usePagination,
	)

	useEffect(() => {
		fetchData && fetchData({pageIndex, pageSize})
	}, [fetchData, pageIndex, pageSize])

	useEffect(() => {
		if (typeof pageNo === "number") gotoPage(pageNo)
	}, [pageNo])

	return (
		<Fragment>
			{loading && <Preloader />}

			{data.length === 0 && !loading && (
				<NoDataWrap>
					<H4 className='m-t-0 m-b-0'>No results found</H4>
				</NoDataWrap>
			)}

			{data.length > 0 && !loading && (
				<Fragment>
					<Table cellspacing='0' {...getTableProps()}>
						<TableHead>
							{headerGroups.map(headerGroup => (
								<TableRow {...headerGroup.getHeaderGroupProps()}>
									{headerGroup.headers.map(column => (
										<TableHeader
											width={column.width}
											{...column.getHeaderProps(column.getSortByToggleProps())}
											onClick={() => column?.toggleSortBy?.(!column.isSortedDesc)}>
											<TableSortedHeader align={alignHeader}>
												{column.render("Header")}
												{column.filter ? column.render('Filter') : null}
												{!column.disableSortBy && (
													<SortIcon>
														{column.isSortedDesc ? (
															<ArrowIcon direction={0} sortingUsed={column.isSorted} />
														) : (
															<ArrowIcon direction={1} sortingUsed={column.isSorted} />
														)}
													</SortIcon>
												)}
											</TableSortedHeader>
										</TableHeader>
									))}
								</TableRow>
							))}
						</TableHead>

						<TableBody {...getTableBodyProps()}>
							{page.map((row, i) => {
								prepareRow(row)
								return (
									<TableRow
										className='tableRow'
										{...row.getRowProps({
											style: {
												// This Applies the background color only to the sub rows. to find the subrows, we are using row depth
												background: row.depth > 0 ? "#f8f8ff" : ""
											}
										})}>
										{row.cells.map((cell, i) => {
											return (
												<TableData
													alignText={i === 1 || i === 2 ? "center" : "left"}
													{...cell.getCellProps({
														style: {wordWrap: "break-word"}
													})}>
													{cell.render("Cell")}
												</TableData>
											)
										})}
									</TableRow>
								)
							})}
						</TableBody>
					</Table>

					{pageCount > 1 && (
						<PaginationContainer>
							<ReactPagination
								breakClassName={styles["page-item"]}
								breakLinkClassName={styles["page-link"]}
								containerClassName={styles.pagination}
								pageClassName={styles["page-item"]}
								pageLinkClassName={styles["page-link"]}
								previousClassName={styles["page-item"]}
								previousLinkClassName={styles["page-link"]}
								nextClassName={styles["page-item"]}
								nextLinkClassName={styles["page-link"]}
								activeClassName={styles.active}
								disabledClassName={styles.disabled}
								pageCount={pageOptions.length}
								forcePage={pageIndex}
								breakLabel={null}
								pageRangeDisplayed={9}
								marginPagesDisplayed={0}
								onPageChange={({selected}) => {
									gotoPage(selected)
									dispatch({
										type: SET_PAGE_INDEX,
										payload: selected
									})
								}}
							/>
						</PaginationContainer>
					)}
					{/* {Boolean(isPaginated) && (
						<Pagination>
							<PaginationIndex>
								page {pageIndex + 1} of {pageOptions.length}
							</PaginationIndex>
							{(canPreviousPage || canNextPage) && (
								<PaginationButtonContainer>
									{canPreviousPage && (
										<PaginationButton onClick={() => previousPage()}>
											<LeftIconSpan>
												<BackButtonIcon />
											</LeftIconSpan>
											Back
										</PaginationButton>
									)}
									{canNextPage && (
										<PaginationButton onClick={() => nextPage()}>
											Next
											<RightIconSpan>
												<NextButtonIcon />
											</RightIconSpan>
										</PaginationButton>
									)}
								</PaginationButtonContainer>
							)}
						</Pagination>
					)} */}
				</Fragment>
			)}
		</Fragment>
	)
}

export default TableComponent
